import type { AdminSession } from '../api/types';

/**
 * Where an admin session survives a reload (02-requirements.md §Admin
 * Authentication, user decision 2026-08-16, reversing "in memory only").
 *
 * **`sessionStorage`, not `localStorage`**, and the difference is the whole
 * decision: the session survives F5, a navigation and a crashed tab restored by
 * the browser, and it dies when the tab is closed. A stolen laptop with a
 * closed browser holds nothing; `localStorage` would keep the token for the
 * full 8 h across every tab and window.
 *
 * **What this costs, stated rather than hidden:** a token in web storage is
 * readable by any script that runs on this origin, so an XSS in the panel now
 * leaks a live admin credential instead of dying with the page. Two things
 * limit it — the only third-party script is Google's sign-in client, loaded by
 * the login screen alone (→ auth/google.ts; still no CDN, no analytics, no font
 * host: §Admin Deployment), and the token still expires in 8 h server-side —
 * and neither removes it. Do not raise this to
 * `localStorage`, and do not extend the API's TTL to compensate.
 */
const KEY = 'camp-enquetes.admin-session';

export interface StoredSession {
  token: string;
  admin: AdminSession['admin'];
}

/**
 * Storage can throw rather than answer: Safari in private mode, an iframe with
 * third-party storage blocked, a full quota. A panel that cannot remember a
 * session must still run, so every path here degrades to "no stored session".
 */
export function readStoredSession(): StoredSession | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (!parsed?.token || !parsed.admin?.id) return null;
    return { token: parsed.token, admin: parsed.admin };
  } catch {
    return null;
  }
}

export function storeSession(session: StoredSession): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    // The session still works in memory for this page; it just will not survive
    // the next reload. Failing the sign-in over it would be worse.
  }
}

export function clearStoredSession(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do: if it cannot be removed it was never written.
  }
}
